/**
 * @file beethoven.ts
 * Fetches attributes for a URL so it can be presented on Padlet.
 */
import environment from '@@/bits/environment'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import type { JsonAPIResource, Link } from '@padlet/arvo'
import { fetchLink } from '@padlet/arvo'
import type { BeethovenDisplayAttributes, BeethovenDisplayOptions } from '@padlet/beethoven-client'
import { getDisplayAttributes as originalGetDisplayAttributes } from '@padlet/beethoven-client'
import { CORS, makeElvisProxyUrl } from '@padlet/elvis-client'
import { memoize } from 'es-toolkit'

const memoizedArvoFetchLink = memoize(fetchLink)

function conditionallyReplacePdfEmbedCode(attributes: BeethovenDisplayAttributes): BeethovenDisplayAttributes {
  // return displayAttributes // uncomment this to not load pdf through /beethoven/pdf-viewer.
  // only pdfs
  if (attributes.content_subcategory !== 'pdf') return attributes

  let pdfViewerHost = 'padlet.com'
  if (environment === 'development') {
    pdfViewerHost = 'padlet.dev'
  } else if (environment === 'staging') {
    pdfViewerHost = 'padlet.io'
  }

  let pdfUrl = attributes.url
  if (attributes.provider_name?.toLowerCase() !== 'padlet drive') {
    // If the provider is not padlet drive (uploaded file), we need to proxy the PDF URL so it can be displayed in an iframe.
    // URLs that have an X-Frame-Options header that disallow a URL from being displayed in an iframe would
    // break our PDF viewer. We proxy the URL via Elvis so the PDF viewer can fetch bytes with CORS.
    pdfUrl = attributes.metadata?.uploaded_copy_url ?? attributes.url
    pdfUrl = makeElvisProxyUrl(pdfUrl, { cors: CORS.ALL })
    // TODO: move all the conditional pdf embedding into @padlet/beethoven-client.
  }
  // Previously when we were using PSPDFKit, the hostname for the iframe had to be padlet.com,
  // because the library was licensed per host and we only bought it for padlet.com.
  // Now, we can set the host to the current host.
  attributes.embed_code = `<iframe class="wb-embed-content" frameborder="0" style="border:0;" width="1024" height="768" src="https://${pdfViewerHost}/beethoven/pdf-viewer?url=${encodeURIComponent(
    pdfUrl,
  )}"></iframe>`
  return attributes
}

const OFFICE_VIEWER_SRC_PATTERN = /src="https?:\/\/view\.officeapps\.live\.com\/op\/embed\.aspx\?src=([^"]+)"/
const GOOGLE_VIEWER_FILE_PATTERN = /\.(docx|pptx)$/i

// Lets us move Office previews off Microsoft's viewer when it is down.
function conditionallyReplaceOfficeEmbedCode(attributes: BeethovenDisplayAttributes): BeethovenDisplayAttributes {
  if (!isAppUsing('useGoogleViewerForMicrosoftFiles')) return attributes

  const match = attributes.embed_code?.match(OFFICE_VIEWER_SRC_PATTERN)
  if (match == null) return attributes

  // The file URL can arrive raw or percent-encoded.
  const src = match[1].replace(/&amp;/g, '&')
  const fileUrl = /^https?%3A/i.test(src) ? decodeURIComponent(src.split('&')[0]) : src
  // Google's viewer renders blank for spreadsheets.
  if (!GOOGLE_VIEWER_FILE_PATTERN.test(fileUrl.split(/[?#]/)[0])) return attributes
  const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`
  attributes.embed_code = attributes.embed_code.replace(match[0], `src="${viewerUrl.replace(/&/g, '&amp;')}"`)
  return attributes
}

async function getDisplayAttributes(
  url: string,
  options: BeethovenDisplayOptions = {},
  attachmentLink: Link | null | undefined = null,
): Promise<BeethovenDisplayAttributes | null> {
  const displayOptions = options
  const fetchLink = memoizedArvoFetchLink
  try {
    let original: BeethovenDisplayAttributes
    if (attachmentLink !== null && attachmentLink !== undefined) {
      original = originalGetDisplayAttributes(attachmentLink, displayOptions)
    } else {
      const link = await fetchLink(url)
      if (link == null) {
        captureMessage('Fetch link from Arvo returned null', { context: { url } })
        return null
      }
      original = originalGetDisplayAttributes((link.data as JsonAPIResource<Link>).attributes, displayOptions)
    }
    return conditionallyReplaceOfficeEmbedCode(conditionallyReplacePdfEmbedCode(original))
  } catch (e) {
    if (e.status === 400) {
      return JSON.parse(e.message)
    }
    return null
  }
}

/**
 * Allows loading PSPDFKit for Web on our electron app
 * We don't want to load PSPDFKit for Electron. If they detects 'Electron/' they will try to load the wrong version.
 * @see https://pspdfkit.com/guides/web/knowledge-base/override-user-agent/
 */
function overrideUserAgentForPspdfkit(): void {
  const currentUserAgent = navigator.userAgent
  Object.defineProperty(navigator, 'userAgent', { get: () => currentUserAgent.replace(/Electron\/.*\s/g, '') })
}

export {
  getDisplayAttributes,
  conditionallyReplaceOfficeEmbedCode,
  conditionallyReplacePdfEmbedCode,
  memoizedArvoFetchLink,
  originalGetDisplayAttributes,
  overrideUserAgentForPspdfkit,
}
